"use client";

import { useEffect, useRef, useState } from "react";
import { hasActiveSubscription } from "@/helpers/hasActiveSubscription";
import { REVIVE_ID, REVIVE_ZONES } from "../../lifestyle-constants";

type ReviveAsyncWindow = Window & {
  reviveAsync?: Record<string, { refresh?: () => void }>;
};

/**
 * A Revive ad placement for the lifestyle grid.
 *
 * Mirrors the Angular `reviveAdEvents` flow: start hidden, call
 * `reviveAsync.refresh()` once the slot is mounted, and only reveal the cell
 * after Revive injects real creative (`a` / `img`).
 *
 * Card: absolute fill so the creative covers the tile without growing the row
 * (img height-% on an auto parent was stretching the grid). Wide: natural
 * image height — no object-cover stretch.
 */
export default function LifestyleAdSlot({ variant }: { variant: "card" | "wide" }) {
  const zones = REVIVE_ZONES[variant];
  const subscriber = hasActiveSubscription();
  const rootRef = useRef<HTMLDivElement>(null);
  const [hasAd, setHasAd] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const isFilled = () => Boolean(root.querySelector("a, img"));

    const revealIfFilled = () => {
      if (!isFilled()) return false;
      setHasAd(true);
      return true;
    };

    if (revealIfFilled()) return;

    const observer = new MutationObserver(() => {
      if (revealIfFilled()) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });

    const refresh = () => (window as ReviveAsyncWindow).reviveAsync?.[REVIVE_ID]?.refresh?.();
    refresh();

    // useDeferredReviveAds may inject the script after this slot mounts.
    const started = Date.now();
    const interval = window.setInterval(() => {
      refresh();
      if (revealIfFilled() || Date.now() - started > 8000) {
        window.clearInterval(interval);
      }
    }, 400);

    return () => {
      observer.disconnect();
      window.clearInterval(interval);
    };
  }, []);

  if (variant === "wide") {
    return (
      <div ref={rootRef} className={`col-span-full w-full ${hasAd ? "" : "hidden"}`}>
        <ins
          data-revive-zoneid={zones.mobile.zoneId}
          data-revive-source={zones.mobile.source}
          data-revive-id={REVIVE_ID}
          data-revive-usersubscriber={subscriber}
          className="block lg:hidden w-full [&_img]:w-full [&_img]:h-auto"
        />
        <ins
          data-revive-zoneid={zones.desktop.zoneId}
          data-revive-source={zones.desktop.source}
          data-revive-id={REVIVE_ID}
          data-revive-usersubscriber={subscriber}
          className="hidden lg:block w-full [&_img]:w-full [&_img]:h-auto"
        />
      </div>
    );
  }

  // Absolute creative: row height comes from neighbouring PrizeCards, not the
  // banner's intrinsic aspect ratio. `a` / `ins` fill that box; img covers it.
  return (
    <div
      ref={rootRef}
      className={`relative h-full min-h-[263px] lg:min-h-[289px] overflow-hidden ${hasAd ? "" : "hidden"}`}>
      <ins
        data-revive-zoneid={zones.mobile.zoneId}
        data-revive-source={zones.mobile.source}
        data-revive-id={REVIVE_ID}
        data-revive-usersubscriber={subscriber}
        className="absolute inset-0 block lg:hidden size-full [&_a]:block [&_a]:size-full [&_img]:size-full [&_img]:object-cover"
      />
      <ins
        data-revive-zoneid={zones.desktop.zoneId}
        data-revive-source={zones.desktop.source}
        data-revive-id={REVIVE_ID}
        data-revive-usersubscriber={subscriber}
        className="absolute inset-0 hidden lg:block size-full [&_a]:block [&_a]:size-full [&_img]:size-full [&_img]:object-cover"
      />
    </div>
  );
}
